import { createElement, type ReactNode } from "react";
import { Check, Clock, Funnel } from "lucide-react";

import {
  AttributionModel,
  GroupDimension,
  ChannelJourneyModel,
} from "@/api/insights";
import { DateFilterOption } from "@/design-system/components/DateFilter/DateFilter";

export const GOOGLE_INTEGRATION_SHORT = "google";
export const META_INTEGRATION_SHORT = "meta";

export const GOOGLE_INTEGRATION_NAME = "Google Ads";
export const META_INTEGRATION_NAME = "Meta Ads";

export const GOOGLE_INTEGRATION_TYPE = "google_ads";
export const META_INTEGRATION_TYPE = "meta_ads";

export const GOOGLE_INTEGRATION_KEY = "google-ads-conversion-api";
export const META_INTEGRATION_KEY = "meta-insights";

export const GOOGLE_INTEGRATION_URL = `/destinations/apps/${GOOGLE_INTEGRATION_KEY}`;
// Was renamed to "Meta Insights" in the UI, but backend type/key is still "Meta Ad Performance".
export const META_INTEGRATION_URL = `/destinations/apps/meta-insights`;

export const STORAGE_KEY_ATTRIBUTION_MODEL = "insightsAttributionModel";
export const STORAGE_KEY_GROUP_DIMENSION = "insightsGroupDimension";
export const STORAGE_KEY_CONSENT_MODE = "insightsConsentMode";
export const STORAGE_KEY_DATE_FILTER = "insightsDateFilter";
export const SOURCE_TO_DESTINATION_MAP: Record<
  string,
  { ctaText: string; destination: "meta" | "google" }
> = {
  "Google Ads": {
    ctaText: "Connect Google Ads",
    destination: GOOGLE_INTEGRATION_SHORT,
  },
  "Meta Insights": {
    ctaText: "Connect Meta",
    destination: META_INTEGRATION_SHORT,
  },
};
export const INTEGRATION_ERROR_MESSAGE = "Error pulling data from ";

export const ENDPOINTS = {
  GET_DYNAMIC_EVENTS: "get_dynamic_events2",
  GET_INTEGRATION_STATUS: "get_integration_stats_pull_status",
  GET_EVENT_DEFS: "get_event_defs",
  GET_EVENT_DEFS_ENRICHED: "get_event_defs_enriched",
  SET_DYNAMIC_EVENTS: "set_dynamic_events",
  GET_INSIGHTS_OVERVIEW: "get_insights_overview",
  GET_JOURNEY_SANKEY: "get_journey_sankey",
  GET_TOP_JOURNEYS: "get_top_journeys",
  GET_CHANNEL_CONTRIBUTION: "get_channel_contribution",
  GET_RECOMMENDATION_CARDS: "get_recommendation_cards_with_state",
  SUBMIT_RECOMMENDATION_CARD_FEEDBACK: "submit_recommendation_card_feedback",
} as const;

export const MULTI_TOUCH_ATTRIBUTIONS = {
  LAST_TOUCH: { key: "last_touch", label: "Last touch" },
  FIRST_TOUCH: { key: "first_touch", label: "First touch" },
  J_SHAPED: { key: "j_shaped", label: "J-shaped" },
  LINEAR: { key: "linear", label: "Linear" },
  U_SHAPED: { key: "u_shaped", label: "U-shaped" },
} as const;

export const GROUP_BY_DIMENSION = {
  CAMPAIGNS: { key: "source", label: "Source & campaign" },
  SERVICE_LINES: { key: "service_line", label: "Service line & campaign" },
  LOCATIONS: { key: "location", label: "Location & campaign" },
} as const;

export const ROUTES = {
  INSIGHTS_HOME: "/insights",
} as const;

// Integration pull status values from the API (keep in sync with backend)
export const INTEGRATION_PULL_STATUS = {
  ERROR: "ERROR",
  UNINITIALIZED: "UNINITIALIZED",
} as const;

// Date filter type values (match DateFilterType from design-system)
export const DATE_FILTER_TYPE = {
  SEVEN_DAYS: "7DaysAgo",
  THIRTY_DAYS: "30DaysAgo",
  THREE_MONTHS: "3MonthsAgo",
  SIX_MONTHS: "6MonthsAgo",
  TWELVE_MONTHS: "12MonthsAgo",
} as const;

// Sort options for EventSearchMultiSelect
export const SORT_BY_OPTIONS = {
  NAME: "name",
  EVENTS_DELIVERED: "eventsDelivered",
  AD_PLATFORMS_DELIVERED: "adPlatformsDelivered",
} as const;

export type SortByOption =
  (typeof SORT_BY_OPTIONS)[keyof typeof SORT_BY_OPTIONS];

export const INSIGHTS_TAB = {
  OVERVIEW: "overview",
  JOURNEYS: "journeys",
} as const;

export type InsightsTab = (typeof INSIGHTS_TAB)[keyof typeof INSIGHTS_TAB];
export const VALID_TABS = new Set<string>(Object.values(INSIGHTS_TAB));

export const JOURNEY_OPTIONS = {
  MULTI: {
    key: "multi",
    label: "Multi-channel journeys",
    description: "Show journeys with touchpoints across multiple channels",
  },
  SINGLE: {
    key: "single",
    label: "Single-channel journeys",
    description: "Show journeys with touchpoints within a single channel",
  },
  ALL: {
    key: "all",
    label: "All journeys",
    description:
      "Include every journey, across single and multiple channels. Use for a complete view of overall user behavior.",
  },
} as const;

export const ATTRIBUTION_MODEL_LABELS: Record<AttributionModel, string> = {
  last_touch: MULTI_TOUCH_ATTRIBUTIONS.LAST_TOUCH.label,
  first_touch: MULTI_TOUCH_ATTRIBUTIONS.FIRST_TOUCH.label,
  linear: MULTI_TOUCH_ATTRIBUTIONS.LINEAR.label,
  u_shaped: MULTI_TOUCH_ATTRIBUTIONS.U_SHAPED.label,
  j_shaped: MULTI_TOUCH_ATTRIBUTIONS.J_SHAPED.label,
};

export const GROUP_DIMENSION_LABELS: Record<GroupDimension, string> = {
  source: GROUP_BY_DIMENSION.CAMPAIGNS.label,
  service_line: GROUP_BY_DIMENSION.SERVICE_LINES.label,
  location: GROUP_BY_DIMENSION.LOCATIONS.label,
};

export const CHANNEL_JOURNEY_LABELS: Record<ChannelJourneyModel, string> = {
  multi: JOURNEY_OPTIONS.MULTI.label,
  single: JOURNEY_OPTIONS.SINGLE.label,
  all: JOURNEY_OPTIONS.ALL.label,
};

export const CHANNEL_JOURNEY_DESCRIPTIONS: Partial<
  Record<ChannelJourneyModel, string>
> = {
  multi: JOURNEY_OPTIONS.MULTI.description,
  single: JOURNEY_OPTIONS.SINGLE.description,
  all: JOURNEY_OPTIONS.ALL.description,
};

export const journeyTypeToOptionKey = {
  all_journeys: JOURNEY_OPTIONS.ALL.key,
  single_source_journeys: JOURNEY_OPTIONS.SINGLE.key,
  multi_source_journeys: JOURNEY_OPTIONS.MULTI.key,
} as const;

export const optionKeyToJourneyType = {
  all: "all_journeys",
  single: "single_source_journeys",
  multi: "multi_source_journeys",
} as const;

export const getDateFilterOptions = (showData: boolean): DateFilterOption[] => [
  {
    type: DATE_FILTER_TYPE.SEVEN_DAYS,
    label: "7D",
    tooltip: "Last 7 days",
    disabled: !showData,
  },
  {
    type: DATE_FILTER_TYPE.THIRTY_DAYS,
    label: "30D",
    tooltip: "Last 30 days",
    disabled: !showData,
  },
  {
    type: DATE_FILTER_TYPE.THREE_MONTHS,
    label: "3M",
    tooltip: "Last 3 months",
    disabled: !showData,
  },
  {
    type: DATE_FILTER_TYPE.SIX_MONTHS,
    label: "6M",
    tooltip:
      "For now, we only pull in 90 days of retroactive data. We're working on expanding this soon!",
    disabled: true,
  },
  {
    type: DATE_FILTER_TYPE.TWELVE_MONTHS,
    label: "12M",
    tooltip:
      "For now, we only pull in 90 days of retroactive data. We're working on expanding this soon!",
    disabled: true,
  },
];

export const STEPPER_STYLES: Record<
  StepStatus,
  { circle: string; label: string; line: string }
> = {
  completed: {
    circle: "bg-green-600",
    label: "font-medium text-green-600",
    line: "border-green-600",
  },
  active: {
    circle: "bg-blue-500 ring-4 ring-blue-700/14 z-2",
    label: "font-semibold text-blue-700",
    line: "border-gray-300 z-1",
  },
  pending: {
    circle:
      "bg-gray-100 border-2 border-gray-200 text-sm font-semibold text-gray-500",
    label: "text-gray-500",
    line: "",
  },
};

export type Phase = "beforeConsent" | "afterConsent" | "waiting";
type StepStatus = "completed" | "active" | "pending";

export interface StepConfig {
  status: StepStatus;
  label: string;
  icon: ReactNode;
}

export const TILT_TRANSFORMS = {
  left: "rotate(-10deg) translate(-60px, -20px)",
  right: "rotate(10deg) translate(60px, -20px)",
} as const;

export const STEPPER_CONFIGS: Record<
  Exclude<Phase, "beforeConsent">,
  StepConfig[]
> = {
  afterConsent: [
    {
      status: "completed",
      label: "AI enabled",
      icon: createElement(Check, {
        className: "h-5 w-5 text-white",
        strokeWidth: 3,
        "aria-hidden": true,
      }),
    },
    {
      status: "active",
      label: "Define your conversion events",
      icon: createElement(Funnel, {
        className: "h-5 w-5 text-white",
        strokeWidth: 2,
        "aria-hidden": true,
      }),
    },
    {
      status: "pending",
      label: "Get weekly recommendations",
      icon: "3",
    },
  ],
  waiting: [
    {
      status: "completed",
      label: "AI enabled",
      icon: createElement(Check, {
        className: "h-5 w-5 text-white",
        strokeWidth: 3,
        "aria-hidden": true,
      }),
    },
    {
      status: "completed",
      label: "Conversion events defined",
      icon: createElement(Check, {
        className: "h-5 w-5 text-white",
        strokeWidth: 2,
        "aria-hidden": true,
      }),
    },
    {
      status: "active",
      label: "Generating recommendations",
      icon: createElement(Clock, {
        className: "h-5 w-5 text-white",
        strokeWidth: 2,
        "aria-hidden": true,
      }),
    },
  ],
};
